// srcs/frontend/src/services/solutionAPI.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/* =========================================================
 * 型定義
 * ========================================================= */

// /seeds/search
export interface SeedsSearchBody {
  company_info: {
    company_name: string;
    industry: string;
    company_size: string;
  };
  needs_info: {
    problem: string;
    ideal_state: string;
    past_efforts: string;
    deadline: string;
    budget: string;
  };
  ai_query_expansion?: boolean;
  top_k?: number;
  alap_flg?: boolean | null;
}
export interface AcceptedResponse {
  session_id: string;
  data_type?: string;
  seed_id?: string;
  status: 'accepted' | string;
}

// /seeds/ranking
export interface SeedsRankingBody {
  session_id: string;
  seed_candidates: { seed_id: string }[];
  ranking_context: {
    evaluation_criteria: {
      similarity_weight: number;
      implementation_weight: number;
      timeliness_weight: number;
      cost_weight: number;
      evolvability_weight: number;
      credibility_weight: number;
    };
  };
}
export interface SeedsRankingItem {
  seed_id: string;
  ranking_score: number;
}

// /roi/data
export interface RoiDataBody {
  session_id: string;
  seed_id: string;
  conversation_history: { role: 'User' | 'Assistant'; content: string }[];
}

// /ai/prompts
export type DataType = 'seeds' | 'gap' | 'roi' | 'proposal';
export interface AiPromptsBody {
  prompt: string;
  session_id: string;
  data_type: DataType;
  seed_id: string | null; // data_type=seeds の時のみ null 許容
}

// /data (GET)
export interface DataGetQuery {
  session_id: string;
  data_type: DataType;
  seed_id: string; // seeds の時は "NULL" 許容
}
export type DataGetResponse = any;

/** ===== /data/ids の新レスポンスに対応する型 ===== */
export type ApiHasBlock =
  | {
      data_type?: 'seeds' | 'gap' | 'roi' | 'proposal' | string;
      seed_id?: string | string[] | null;     // "NULL" 文字列が来る場合あり
      has_analysis_result?: boolean;          // ✓/✗ の判定に使用
      timestamp?: string | number | null;     // UNIX秒（文字列/数値）
    }
  | Record<string, never>;                    // 空オブジェクト {} も来る

// /data/ids (GET)
export interface DataIdsQuery {
  company_name?: string;
  user_mail_address?: string;
}
export interface DataIdsItem {
  session_id: string;

  has_seeds: ApiHasBlock;
  has_gap: ApiHasBlock;
  has_roi: ApiHasBlock;
  has_proposal: ApiHasBlock;

  user_mail_address: string;
  company_name: string;
  industry: string;
  company_size: string;
}

// /data/clear (PUT) —— session_id のみでも呼べるように
export interface DataClearBody {
  session_id: string;
  data_type?: DataType;
  seed_id?: string; // "NULL" を含む任意
}
export interface DataClearResponse {
  session_id: string;
  status: 'cleared' | string;
}

// /files (POST/GET)
export interface FilesPostBody {
  file_name: string;
  file_context: string; // 保存するJSON文字列
}
export interface FilesGetQuery {
  file_name: string;
}
export interface FilesResponse {
  status: number | string;
  file_name: string;
  file_context?: string; // GET /filesで返る
}

// /files/by-data (GET)
export interface FilesByDataQuery {
  session_id: string;
  data_type: DataType;
  seed_id: string;
}
export interface FilesByDataResponse {
  session_id: string;
  data_type: DataType;
  seed_id: string;
  file_names: string[];
}

/* =========================================================
 * baseApi（参考コードスタイル）
 *  - prepareHeaders の中で idToken を取得・設定
 * ========================================================= */
export const baseApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_API,
    prepareHeaders: async (headers, _) =>  {
      const queryParams = new URLSearchParams(window.location.search);
      const idToken = queryParams.get("idToken")
      const postMessageHandler = (e:any) => {
        if (e.data.type && "postIdToken" === e.data.type) {
          console.log("postMessageを受け取ったイベント情報：", e.data);
          localStorage.setItem('idToken', `${e.data.idToken}`);
        }
      };
      window.addEventListener("message", postMessageHandler);

      if(idToken){
        headers.set('Authorization', `${idToken}`)
        localStorage.setItem('idToken', `${idToken}`);
      } else {
        const idToken = localStorage.getItem('idToken');
        headers.set('Authorization', `${idToken}`)
      }
      return headers
    },
    mode: 'cors'
  }),
  endpoints: (builder) => ({
    // --- ニーズ・シーズマッチング ---
    seedsSearch: builder.mutation<AcceptedResponse, SeedsSearchBody>({
      query: (body) => ({
        url: `seeds/search`,
        method: 'POST',
        body,
      }),
    }),
    seedsRanking: builder.mutation<SeedsRankingItem[], SeedsRankingBody>({
      query: (body) => ({
        url: `seeds/ranking`,
        method: 'POST',
        body,
      }),
    }),

    // --- ROI試算 ---
    roiData: builder.mutation<AcceptedResponse, RoiDataBody>({
      query: (body) => ({
        url: `roi/data`,
        method: 'POST',
        body,
      }),
    }),

    // --- 生成AI（共通） ---
    aiPrompts: builder.mutation<AcceptedResponse, AiPromptsBody>({
      query: (body) => ({
        url: `ai/prompts`,
        method: 'POST',
        body,
      }),
    }),

    // --- 分析管理 ---
    getData: builder.query<DataGetResponse, DataGetQuery>({
      query: (params) =>
        `data?${new URLSearchParams(params as any).toString()}`,
    }),
    getDataIds: builder.query<DataIdsItem[], DataIdsQuery>({
      query: (params) =>
        `data/ids?${new URLSearchParams(params as any).toString()}`,
    }),
    clearData: builder.mutation<DataClearResponse, DataClearBody>({
      query: (body) => ({
        url: `data/clear`,
        method: 'PUT',
        body,
      }),
    }),

    // --- ファイル入出力 ---
    importFile: builder.mutation<FilesResponse, FilesPostBody>({
      query: (body) => ({
        url: `files`,
        method: 'POST',
        body,
      }),
    }),
    getFile: builder.query<FilesResponse, FilesGetQuery>({
      query: (params) =>
        `files?${new URLSearchParams(params as any).toString()}`,
    }),
    getFilesByData: builder.query<FilesByDataResponse, FilesByDataQuery>({
      query: (params) =>
        `files/by-data?${new URLSearchParams(params as any).toString()}`,
    }),
  }),
});

/* =========================================================
 * Hooks（参考コードの命名ルールに準拠）
 * ========================================================= */
export const {
  useSeedsSearchMutation,
  useSeedsRankingMutation,
  useRoiDataMutation,
  useAiPromptsMutation,
  useLazyGetDataQuery,
  useLazyGetDataIdsQuery,
  useClearDataMutation,
  useImportFileMutation,
  useLazyGetFileQuery,
  useLazyGetFilesByDataQuery,
} = baseApi;
